/**
 * Public Privacy Policy page — qdrives.in/privacy-policy.
 *
 * Corporate, static content owned by Q Drives Automotive Private Limited.
 * No auth. No data fetching. Content matches the version approved for
 * publication on Q Drives' website and for external platforms (LinkedIn,
 * Meta, etc.) referencing this URL.
 */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

const UPDATED_ON = "8 September 2026";
const CONTACT_EMAIL = "enquiry@qdrives.co.in";
const CANONICAL_URL = "https://qdrives.in/privacy-policy";
const PAGE_TITLE = "Privacy Policy | Q Drives Automotive Private Limited";
const PAGE_DESCRIPTION =
  "Privacy Policy of Q Drives Automotive Private Limited explaining how information " +
  "submitted through our website, LinkedIn forms, advertisements and business " +
  "enquiries is collected, used, shared and protected.";

/** Small DOM helper — sets or replaces a <meta> / <link> tag in <head>. */
function upsertHeadTag(tag, attrs) {
  const selector = tag + Object.entries(attrs)
    .filter(([k]) => k !== "content" && k !== "href")
    .map(([k, v]) => `[${k}="${v}"]`).join("");
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  }
  return el;
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="space-y-3" data-testid={`policy-section-${id}`}>
      <h2 className="text-base sm:text-lg font-display tracking-wide text-white">{title}</h2>
      <div className="text-sm text-white/80 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;
    const descEl = upsertHeadTag("meta", { name: "description", content: PAGE_DESCRIPTION });
    const canonEl = upsertHeadTag("link", { rel: "canonical", href: CANONICAL_URL });
    // Explicit robots directive — never block indexing on the legal page.
    const robotsEl = upsertHeadTag("meta", { name: "robots", content: "index, follow" });
    return () => {
      document.title = prevTitle;
      // Leave meta tags in place — they'll be overwritten by the next page.
      void descEl; void canonEl; void robotsEl;
    };
  }, []);

  return (
    <main
      className="min-h-screen bg-[#0A0A0A] text-white px-4 sm:px-8 pt-8 pb-24"
      data-testid="privacy-policy-page"
    >
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="back-link"
          className="inline-flex items-center gap-2 text-[11px] font-display tracking-widest uppercase text-white/55 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> Back to Home
        </Link>

        <header className="mt-6 mb-8 border-b border-[#222] pb-6">
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/45">
            Q Drives Automotive Private Limited · Legal
          </div>
          <h1 className="text-3xl sm:text-4xl font-display tracking-wide text-white mt-2">
            Privacy Policy
          </h1>
          <div className="text-xs text-white/45 font-mono-data mt-2">
            Last Updated: {UPDATED_ON}
          </div>
        </header>

        <div className="space-y-8">
          <p className="text-sm text-white/80 leading-relaxed">
            Q Drives Automotive Private Limited (“Q Drives”, “we”, “us”, or “our”) respects your
            privacy and is committed to handling personal information responsibly.
          </p>
          <p className="text-sm text-white/80 leading-relaxed">
            This Privacy Policy explains how Q Drives Automotive Private Limited collects, uses,
            stores, shares, and protects personal and business information received through our
            website, LinkedIn Lead Gen Forms, advertising campaigns, social media, business
            enquiries, partnership enquiries, WhatsApp, email, telephone, and other communication
            channels.
          </p>
          <p className="text-sm text-white/80 leading-relaxed">
            By voluntarily submitting information to Q Drives, you acknowledge that the information
            may be handled in accordance with this Privacy Policy.
          </p>

          <Section id="information-we-collect" title="1. Information We Collect">
            <p>
              Q Drives Automotive Private Limited may collect information voluntarily submitted by
              individuals, customers, applicants, prospective partners, business representatives,
              or other persons interacting with Q Drives.
            </p>
            <p>This may include:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Full name</li>
              <li>Phone number</li>
              <li>Email address</li>
              <li>Company or organisation name</li>
              <li>Job title or professional information</li>
              <li>City, country, or location</li>
              <li>Business or partnership details</li>
              <li>Information submitted through LinkedIn Lead Gen Forms</li>
              <li>Responses to questions contained in advertisements or enquiry forms</li>
              <li>Vehicle-related information where relevant</li>
              <li>Information provided through WhatsApp, email, telephone, social media, or our website</li>
              <li>Any other information voluntarily provided to Q Drives</li>
            </ul>
            <p>
              We may also collect basic technical or usage information generated through our
              website or technology providers where applicable.
            </p>
          </Section>

          <Section id="linkedin-lead-gen-forms" title="2. LinkedIn Lead Gen Forms">
            <p>
              When you submit information through a Q Drives advertisement or Lead Gen Form on
              LinkedIn, Q Drives Automotive Private Limited may receive information that you have
              chosen to provide through LinkedIn.
            </p>
            <p>This may include:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Name</li>
              <li>Phone number</li>
              <li>Email address</li>
              <li>Company information</li>
              <li>Professional information</li>
              <li>Location</li>
              <li>Responses to form questions</li>
              <li>Partnership or business-related information</li>
            </ul>
            <p>
              Q Drives may use this information to contact you, review your enquiry, assess a
              potential partnership or business relationship, provide information about Q Drives
              services, and maintain appropriate business records.
            </p>
            <p>
              Your use of LinkedIn is separately governed by LinkedIn’s own terms, policies, and
              privacy practices.
            </p>
          </Section>

          <Section id="how-we-use-information" title="3. How We Use Information">
            <p>Q Drives Automotive Private Limited may use collected information to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Contact prospective business partners</li>
              <li>Contact applicants or prospective customers</li>
              <li>Review and assess partnership enquiries</li>
              <li>Evaluate potential commercial relationships</li>
              <li>Respond to business enquiries</li>
              <li>Communicate regarding Q Drives products, services, opportunities, and operations</li>
              <li>Provide requested information</li>
              <li>Conduct appropriate follow-up communication</li>
              <li>Manage customer or business relationships</li>
              <li>Maintain internal business and enquiry records</li>
              <li>Improve our services and operations</li>
              <li>Improve customer and partner experience</li>
              <li>Prevent misuse, fraud, or unauthorised activity</li>
              <li>Comply with applicable legal, accounting, regulatory, contractual, or administrative requirements</li>
              <li>Establish, exercise, or defend legitimate business or legal interests</li>
            </ul>
            <p>
              Submission of an enquiry does not by itself create any partnership, agency,
              franchise, employment, investment, contractual, or commercial relationship with
              Q Drives Automotive Private Limited.
            </p>
            <p>Any such relationship will only arise if separately agreed in writing.</p>
          </Section>

          <Section id="sale-of-personal-information" title="4. Sale of Personal Information">
            <p>
              Q Drives Automotive Private Limited does not sell personal information to third
              parties.
            </p>
            <p>
              Personal information collected through LinkedIn Lead Gen Forms, our website,
              advertisements, or business enquiries will not be sold or commercially traded as
              personal data to unrelated third parties.
            </p>
          </Section>

          <Section id="sharing-of-information" title="5. Sharing of Information">
            <p>
              Q Drives may share information with trusted third-party service providers only where
              reasonably necessary for legitimate business operations.
            </p>
            <p>These may include providers supporting:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Website hosting</li>
              <li>Cloud infrastructure</li>
              <li>CRM systems</li>
              <li>Communications</li>
              <li>Email</li>
              <li>Messaging</li>
              <li>Advertising</li>
              <li>Lead management</li>
              <li>Analytics</li>
              <li>Technology infrastructure</li>
              <li>Accounting</li>
              <li>Legal services</li>
              <li>Professional advisory services</li>
              <li>Customer or enquiry management</li>
            </ul>
            <p>
              Such information should only be shared to the extent reasonably necessary for the
              relevant business purpose.
            </p>
            <p>
              Q Drives may also disclose information where required or permitted by applicable law,
              regulation, court order, legal process, government authority, or where reasonably
              necessary to protect the rights, property, security, or legitimate interests of
              Q Drives Automotive Private Limited.
            </p>
          </Section>

          <Section id="data-security" title="6. Data Security">
            <p>
              Q Drives Automotive Private Limited uses reasonable administrative, organisational,
              and technical measures intended to protect personal information against:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Unauthorised access</li>
              <li>Misuse</li>
              <li>Loss</li>
              <li>Alteration</li>
              <li>Disclosure</li>
              <li>Destruction</li>
            </ul>
            <p>
              However, no internet transmission, electronic communication system, or data storage
              system can be guaranteed to be completely secure.
            </p>
          </Section>

          <Section id="data-retention" title="7. Data Retention">
            <p>
              Q Drives may retain personal information for as long as reasonably necessary for the
              purposes for which it was collected.
            </p>
            <p>This may include retention required to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Process an enquiry</li>
              <li>Manage an ongoing business relationship</li>
              <li>Maintain legitimate business records</li>
              <li>Conduct appropriate follow-up</li>
              <li>Meet contractual obligations</li>
              <li>Meet legal or regulatory requirements</li>
              <li>Maintain accounting or administrative records</li>
              <li>Resolve disputes</li>
              <li>Protect legitimate business interests</li>
            </ul>
            <p>
              When personal information is no longer reasonably required, Q Drives may delete,
              anonymise, archive, or otherwise securely dispose of it, subject to applicable legal
              or business requirements.
            </p>
          </Section>

          <Section id="correction-access-deletion" title="8. Correction, Access, and Deletion Requests">
            <p>Individuals may contact Q Drives Automotive Private Limited to request:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Correction of inaccurate personal information</li>
              <li>Updating of incomplete information</li>
              <li>Deletion of personal information, where legally and operationally permissible</li>
              <li>Information regarding personal data held by Q Drives</li>
              <li>Withdrawal from certain communications</li>
              <li>Clarification regarding how their personal information is being handled</li>
            </ul>
            <p>
              Q Drives may request reasonable information to verify the identity of the person
              making the request before processing the request.
            </p>
            <p>
              Deletion requests may be subject to legitimate legal, regulatory, contractual,
              accounting, fraud-prevention, or business record-keeping requirements.
            </p>
          </Section>

          <Section id="communications" title="9. Communications">
            <p>Where permitted, Q Drives may contact individuals regarding:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Their submitted enquiry</li>
              <li>Partnership opportunities</li>
              <li>Business discussions</li>
              <li>Q Drives services</li>
              <li>Relevant updates</li>
              <li>Follow-up communications</li>
            </ul>
            <p>
              Individuals may request that certain non-essential communications stop by contacting
              Q Drives.
            </p>
          </Section>

          <Section id="third-party-platforms" title="10. Third-Party Platforms">
            <p>
              Q Drives may use third-party platforms and technology providers to operate its
              business.
            </p>
            <p>These may include:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>LinkedIn</li>
              <li>Meta platforms</li>
              <li>WhatsApp</li>
              <li>Website hosting services</li>
              <li>CRM providers</li>
              <li>Analytics platforms</li>
              <li>Advertising platforms</li>
              <li>Communication providers</li>
              <li>Cloud infrastructure providers</li>
            </ul>
            <p>
              Information processed through these services may also be subject to the privacy
              policies, terms, and data-handling practices of the respective third-party provider.
            </p>
          </Section>

          <Section id="international-processing" title="11. International Processing">
            <p>
              Where Q Drives or its service providers operate across multiple jurisdictions,
              information may be processed or stored in jurisdictions other than the location from
              which it was originally submitted.
            </p>
            <p>
              Where applicable, Q Drives will take reasonable measures appropriate to the nature of
              the information and the relevant business activity.
            </p>
          </Section>

          <Section id="changes-to-this-policy" title="12. Changes to This Privacy Policy">
            <p>
              Q Drives Automotive Private Limited may update this Privacy Policy periodically to
              reflect changes in:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Business operations</li>
              <li>Services</li>
              <li>Technology</li>
              <li>Legal requirements</li>
              <li>Regulatory requirements</li>
              <li>Data-handling practices</li>
            </ul>
            <p>The current version will always be published on:</p>
            <p>
              <a
                href={CANONICAL_URL}
                className="text-white underline decoration-white/30 hover:decoration-white break-all"
                data-testid="canonical-link"
              >
                {CANONICAL_URL}
              </a>
            </p>
            <p>
              The “Last Updated” date at the top of the page should be changed whenever the Privacy
              Policy is materially updated.
            </p>
          </Section>

          <Section id="contact" title="13. Contact for Privacy and Data Requests">
            <p>The entity responsible for the information described in this Privacy Policy is:</p>
            <p className="text-white font-medium">Q Drives Automotive Private Limited</p>
            <p>
              <span className="text-white/60">Website:</span>{" "}
              <a
                href="https://qdrives.in"
                className="text-white underline decoration-white/30 hover:decoration-white"
              >
                https://qdrives.in
              </a>
            </p>
            <p className="text-white/80">Privacy and data-related requests:</p>
            <p>
              <span className="text-white/60">Email:</span>{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="privacy-contact-email"
                className="text-white underline decoration-white/30 hover:decoration-white break-all"
              >
                {CONTACT_EMAIL}
              </a>
            </p>
            <p>
              Users may contact Q Drives through this email to request correction, access,
              deletion, or clarification regarding their personal information.
            </p>
          </Section>

          <div className="text-[10px] text-white/35 font-mono-data tracking-widest uppercase pt-6 border-t border-[#1A1A1A]">
            © Q Drives Automotive Private Limited · qdrives.in
          </div>
        </div>
      </div>
    </main>
  );
}
